import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Eye } from "lucide-react";
import { Student } from "@/types";

interface ActiveProfilesListProps {
  schoolId: string;
  currentTermId: string;
  onViewProfile: (student: Student) => void;
  onEditProfile: (profile: any, student: Student) => void;
}

export function ActiveProfilesList({ 
  schoolId, 
  currentTermId, 
  onViewProfile,
  onEditProfile 
}: ActiveProfilesListProps) {
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['activeMonitoringProfiles', schoolId, currentTermId],
    queryFn: async () => {
      const { data } = await supabase
        .from('special_monitoring_profiles')
        .select(`
          *,
          students (
            id,
            surname,
            other_names,
            class,
            stream,
            medical_alerts,
            school_id,
            created_at,
            updated_at
          )
        `)
        .eq('school_id', schoolId)
        .eq('term_id', currentTermId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      return data || [];
    },
    enabled: !!schoolId && !!currentTermId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading active profiles...
        </CardContent>
      </Card>
    );
  }

  if (profiles.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No active monitoring profiles for this term. Search for a student to create a new profile.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Active Monitoring Profiles</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {profiles.map((profile: any) => {
          const student = profile.students;
          if (!student) return null;

          const studentData: Student = {
            id: student.id,
            surname: student.surname,
            other_names: student.other_names,
            class: student.class,
            stream: student.stream,
            medical_alerts: student.medical_alerts || [],
            school_id: student.school_id,
            created_at: student.created_at,
            updated_at: student.updated_at,
          };

          return (
            <Card key={profile.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {student.surname}, {student.other_names}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {student.class} {student.stream}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <span className="font-medium">Parameters: </span>
                  {(profile.parameters as any[])?.length || 0}
                </div>
                {profile.notes && (
                  <div className="text-sm text-muted-foreground line-clamp-2">
                    {profile.notes}
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => onViewProfile(studentData)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => onEditProfile(profile, studentData)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
